import type { ReactNode } from 'react';

import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { dailyTasksApi } from '@/api/dailyTasks';
import { taskLinkFor } from '@/lib/taskLinks';
import { useAuthStore } from '@/stores/auth';
import { useCuratorChat } from '@/hooks/useCuratorChat';
import { teacherAvatarOf, teacherNameOf } from '@/lib/studentFields';
import { useWeekEvents } from '@/hooks/useWeekEvents';
import { deadlineNear, isPast } from '@/lib/calendarItems';
import { cn } from '@/lib/utils';

import { WordOfTheDayCard } from '../WordOfTheDayCard';

/**
 * Общие виджеты правой колонки: куратор, слово дня, календарь недели, задания
 * на сегодня.
 *
 * ⚠️ ПОЧЕМУ ОТДЕЛЬНЫМ ФАЙЛОМ, А НЕ КОПИЕЙ В КАЖДОМ РАЗДЕЛЕ. 07.09.2026 заказчик
 * попросил те же виджеты в тренажёрах, что и в «Учёбе», и прямо сказал:
 * «посмотри как они устроены в других секциях». Скопировать было бы быстрее, но
 * копии расходятся: правка формулировки у куратора или починка календаря
 * досталась бы одному разделу и обошла второй. Ровно так у нас разъехались три
 * шкалы band и правило расписания тренажёров.
 *
 * ⚠️ КАРТОЧКА ПОКАЗЫВАЕТСЯ, ТОЛЬКО КОГДА ДЛЯ НЕЁ ЕСТЬ ДАННЫЕ. Пустая рамка с
 * заголовком читается как поломка, а придуманные значения хуже пустоты.
 */
export function StudentSideWidgets({
  marathonIds,
  afterCurator,
  withCurator = true,
}: {
  marathonIds: string[];
  /**
   * ⚠️ ПОКАЗЫВАТЬ ЛИ КАРТОЧКУ КУРАТОРА. В чате её быть не должно: куратор там
   * уже есть и своей карточкой сверху, и живым собеседником в переписке.
   * Показать её значило бы предложить кнопку «Написать куратору» человеку,
   * который ему прямо сейчас пишет.
   */
  withCurator?: boolean;
  /**
   * ⚠️ ЧТО ВСТАВИТЬ СРАЗУ ПОСЛЕ КАРТОЧКИ КУРАТОРА.
   *
   * Нужен ровно затем, чтобы вынос виджетов в общий файл НЕ ПЕРЕСТАВИЛ готовый
   * экран. В «Учёбе» уровни по навыкам стоят вторыми, сразу за куратором; без
   * этой щели они уехали бы в самый низ, под задания, — перестановка рабочего
   * экрана, которой никто не просил. Аудит 07.09.2026 это и поймал.
   */
  afterCurator?: ReactNode;
}) {
  const { t, i18n } = useTranslation();
  const user = useAuthStore((st) => st.user);

  // Карточка куратора — только при праве chat и назначенном кураторе (решение владельца 14.09.2026).
  const { curatorChat, showCuratorEntry } = useCuratorChat();
  const { today, days, eventsOf, setPicked, shownDay, upcomingList } = useWeekEvents();
  const tasks = useQuery({
    queryKey: ['student', 'daily-tasks', marathonIds],
    queryFn: () => dailyTasksApi.today({ marathonIds }),
  });

  const curator = teacherNameOf(curatorChat);
  const avatar = teacherAvatarOf(curatorChat);

  const initials = (curator ?? t('chat.curator'))
    .trim()
    .split(/\s+/)
    .map((ч) => ч[0] ?? '')
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <>
      {withCurator && showCuratorEntry && (
        <>
      {/* ── Куратор ─────────────────────────────────────────────────────────
          ⚠️ ВОПРОС ВМЕСТО ЯРЛЫКА. Раньше карточка сообщала «Кураторың» — просто
          подпись, из которой не ясно, зачем сюда нажимать. Заказчик 06.09.2026
          попросил спрашивать по делу: «Возникли трудности или вопросы?» — и
          кнопку «Написать куратору».

          ⚠️ ФОТО ПОКАЗЫВАЕМ, ЕСЛИ ОНО ЕСТЬ. До 06.09.2026 фото не приходило с
          сервера вовсе, и ученик всегда видел только буквы. Буквы остаются
          запасным вариантом: фото есть у одного куратора из шестидесяти
          девяти, и это НОРМА, а не сбой. */}
      <div className="rounded-[20px] bg-surface-container px-[18px] py-4">
        <div className="flex items-center gap-3">
          <span className="grid h-11 w-11 flex-none place-items-center overflow-hidden rounded-full bg-info-container text-[13px] font-bold text-info">
            {avatar ? <img src={avatar} alt="" className="h-full w-full object-cover" /> : initials}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[13.5px] font-semibold text-on-surface">
              {curator ?? t('chat.curator')}
            </span>
            <span className="block text-[11.5px] text-on-surface-variant">{t('learn.curatorAsk')}</span>
          </span>
        </div>
        <Link
          to="/app/chat"
          className="mt-3 block rounded-full bg-info py-[9px] text-center text-[12.5px] font-bold text-white transition hover:brightness-110"
        >
          {t('learn.curatorWrite')}
        </Link>
      </div>

        </>
      )}

      {afterCurator}

      {/* Слово дня даёт занятие на минуту, когда браться за большое не хочется.
          Запрос тот же, что на главной, — второй волны обращений не будет. */}
      <WordOfTheDayCard tone="info" />

      {/* ── Календарь недели ────────────────────────────────────────────────
          ⚠️ ЭТО НАСТОЯЩИЙ КАЛЕНДАРЬ, А НЕ ССЫЛКА. Первая попытка 06.09.2026
          показывала одну строку «занятий нет» — заказчик прислал макет и
          сказал: имел в виду вот такой. Неделя с числами и подсветкой сегодня
          читается с одного взгляда, строка — нет.

          Нажатие выбирает день ЗДЕСЬ ЖЕ, а не уводит в расписание: то же
          решение, что и на телефоне (24.08.2026).
          Карточка есть и в приложении: календарь вернулся в сборку 1.1.1 (02.10.2026). */}
      <div className="rounded-[20px] bg-surface-container px-[18px] py-4">
        <div className="mb-3 flex items-baseline gap-3">
          <h3 className="flex-1 font-display text-[14.5px] font-bold capitalize text-on-surface">
            {shownDay.toLocaleDateString(i18n.language, { month: 'long' })}
          </h3>
          <Link to="/app/schedule" className="text-[12.5px] font-semibold text-info hover:underline">
            {t('nav.schedule')} →
          </Link>
        </div>

        <div className="grid grid-cols-7 gap-1">
          {days.map((d) => (
            <span
              key={`h-${d.getTime()}`}
              className="text-center text-[10.5px] lowercase text-on-surface-variant"
            >
              {d.toLocaleDateString(i18n.language, { weekday: 'short' })}
            </span>
          ))}
          {days.map((d) => {
            const isShown = d.toDateString() === shownDay.toDateString();
            const isToday = d.toDateString() === today.toDateString();
            const has = eventsOf(d).length > 0;
            return (
              <button
                key={d.getTime()}
                type="button"
                onClick={() => setPicked(d)}
                className={cn(
                  'relative grid h-[30px] w-full place-items-center rounded-full text-[13px] font-bold tabular-nums transition',
                  isShown
                    ? 'bg-primary text-on-primary'
                    : isToday
                      ? 'text-primary hover:bg-surface-container-high'
                      : 'text-on-surface hover:bg-surface-container-high',
                )}
              >
                {d.getDate()}
                {/* Точка — «в этот день есть занятие». Без неё календарь
                    показывает только числа, которые ученик и так знает. */}
                {has && !isShown && (
                  <span className="absolute bottom-[3px] h-[3px] w-[3px] rounded-full bg-primary" />
                )}
              </button>
            );
          })}
        </div>

        <div className="mt-3 border-t border-outline pt-3">
          {/* ⚠️ ДО ТРЁХ БЛИЖАЙШИХ, А НЕ ОДНО. Заказчик 07.09.2026: «календарь
              дисплеит только 1 задачку, давай сделаем хотя бы 3». Начавшееся
              сегодня не прячется, а стоит ниже и приглушено (02.10.2026):
              впереди идущие первыми — три строки не займёт то, куда поздно. */}
          {upcomingList.length > 0 ? (
            <div className="flex flex-col gap-2.5">
              {upcomingList.map((ev) => (
                <Link
                  key={ev.id}
                  to="/app/schedule"
                  className={cn('flex items-center gap-[11px]', isPast(ev, today) && 'opacity-50')}
                >
                  <span className="grid h-[34px] w-[34px] flex-none place-items-center rounded-xl bg-primary-container text-[10.5px] font-bold text-on-primary-container">
                    {ev.start.toLocaleTimeString(i18n.language, {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[12.5px] font-semibold text-on-surface">
                      {ev.title}
                    </span>
                    <span className="block text-[11px] text-on-surface-variant">
                      {ev.start.toLocaleDateString(i18n.language, {
                        weekday: 'short',
                        day: 'numeric',
                        month: 'long',
                      })}
                      {deadlineNear(ev, today) ? ` · ${t('schedule.deadlineNear')}` : ''}
                    </span>
                  </span>
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-[12px] text-on-surface-variant">
              {t('learn.noPlansOn', {
                date: shownDay.toLocaleDateString(i18n.language, { day: 'numeric', month: 'long' }),
              })}
            </p>
          )}
        </div>
      </div>

      {/* ── Задания на сегодня ─────────────────────────────────────────────── */}
      {tasks.data && tasks.data.length > 0 && (
        <div>
          <div className="mx-0.5 mb-2 flex items-baseline gap-3">
            <h3 className="flex-1 font-display text-[14.5px] font-bold text-on-surface">
              {t('learn.todayTasks')}
            </h3>
            <span className="text-[12.5px] font-semibold text-on-surface-variant tabular-nums">
              {tasks.data.filter((x) => x.done).length}/{tasks.data.length}
            </span>
          </div>
          <div className="flex flex-col gap-2">
            {tasks.data.slice(0, 4).map((task) => {
              // В приложении — только туда, куда ученик попадёт (`lib/taskLinks.ts`).
              const to = taskLinkFor(task, user);
              const row = (
                <span className="flex items-center gap-3 rounded-[20px] bg-surface-container px-[18px] py-3.5">
                  {/* ⚠️ КРУЖОК РИСУЕМ РАМКОЙ, А НЕ ЗНАЧКОМ. Значка
                      `radio_button_unchecked` в нашем урезанном шрифте нет, и
                      06.09.2026 вместо него на экране вылезла надпись
                      RADIO_BUTTON_UNCHECKED. */}
                  {task.done ? (
                    <span className="material-symbols-outlined flex-none text-[19px] text-primary">
                      check_circle
                    </span>
                  ) : (
                    <span className="h-[18px] w-[18px] flex-none rounded-full border-2 border-info" />
                  )}
                  <span
                    className={cn(
                      'min-w-0 flex-1 truncate text-[13px]',
                      task.done ? 'text-on-surface-variant line-through' : 'font-semibold text-on-surface',
                    )}
                  >
                    {task.title}
                  </span>
                  {to && (
                    <span className="material-symbols-outlined flex-none text-[16px] text-on-surface-variant">
                      chevron_right
                    </span>
                  )}
                </span>
              );
              // Задание-напоминание («повторить глаголы») ссылки не имеет
              // намеренно: переход в никуда хуже его отсутствия.
              return to ? (
                <Link key={task.id} to={to} className="hover:opacity-90">
                  {row}
                </Link>
              ) : (
                <span key={task.id}>{row}</span>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}
